import React, { useState, useEffect, useMemo } from 'react';
import {
  ArrowRight,
  Award,
  BarChart,
  Bot,
  Briefcase,
  Check,
  ClipboardCheck,
  Home,
  PhoneCall,
  ShoppingCart,
  TrendingUp,
  Users,
  UtensilsCrossed,
  Workflow,
  X as CloseIcon,
  Zap
} from 'lucide-react';
import { sendContactForm } from '../services/emailService';
import InventoryMarginDashboard from '../components/InventoryMarginDashboard';

const industrySolutions = [
  {
    name: 'Retail & E-commerce',
    icon: ShoppingCart,
    outcome: 'Protect margin while avoiding stockouts.',
    examples: ['Inventory vs margin dashboards', 'Margin leak alerts', 'Slow-moving stock detection']
  },
  {
    name: 'Trades & Home Services',
    icon: Home,
    outcome: 'Turn missed calls and quotes into booked work.',
    examples: ['Private knowledge repository for training and onsite help', 'Quote follow-up automation', 'Job scheduling alerts']
  },
  {
    name: 'Professional Services',
    icon: Briefcase,
    outcome: 'Spend less time on admin and more time serving clients.',
    examples: ['Client onboarding', 'Meeting summaries', 'Billable time visibility']
  },
  {
    name: 'Restaurants & Hospitality',
    icon: UtensilsCrossed,
    outcome: 'Match staffing, menu margin, and booking demand.',
    examples: ['Demand forecasting', 'Menu margin analysis', 'Review sentiment monitoring']
  }
];

const automationFlow = [
  { icon: PhoneCall, title: 'A request comes in', detail: 'Email, form, missed call, or booking request is captured automatically.' },
  { icon: Bot, title: 'AI gathers the details', detail: 'Customer history, stock, calendar, and open jobs are checked in the background.' },
  { icon: ClipboardCheck, title: 'The next step is prepared', detail: 'A quote, reply, task, or alert is ready for your team to review.' },
  { icon: ArrowRight, title: 'You stay in control', detail: 'Your team approves, edits, or automates the next step.' }
];

const workflowComparisons = [
  {
    topic: 'Lead handling',
    before: 'Leads sit in email until someone notices.',
    after: 'Priority leads are scored and routed.'
  },
  {
    topic: 'Inventory decisions',
    before: 'Stock decisions happen from memory.',
    after: 'Stock risks and margin pressure are flagged early.'
  },
  {
    topic: 'Reporting',
    before: 'Reports are rebuilt manually each week.',
    after: 'Dashboards refresh from live business systems.'
  },
  {
    topic: 'Follow-up',
    before: 'Quotes and replies depend on spare time.',
    after: 'Draft replies, tasks, and alerts are ready for approval.'
  }
];

const consultationSteps = [
  {
    field: 'name',
    question: 'Hi, I can help you book a free consultation. What should we call you?',
    type: 'text',
    inputType: 'text',
    placeholder: 'Your name',
    required: true
  },
  {
    field: 'email',
    question: 'What email should we use to reply?',
    type: 'text',
    inputType: 'email',
    placeholder: 'you@company.com',
    required: true
  },
  {
    field: 'company',
    question: 'What is your business called?',
    type: 'text',
    inputType: 'text',
    placeholder: 'Company name',
    optionalLabel: 'Skip for now'
  },
  {
    field: 'phone',
    question: 'What phone number should we use?',
    type: 'text',
    inputType: 'tel',
    placeholder: 'Phone number',
    optionalLabel: 'Email only is fine'
  },
  {
    field: 'industry',
    question: 'What kind of business do you run?',
    type: 'options',
    otherField: 'industryOther',
    options: [
      { value: 'retail-ecommerce', label: 'Retail & E-commerce' },
      { value: 'trades-home-services', label: 'Trades & Home Services' },
      { value: 'professional-services', label: 'Professional Services' },
      { value: 'restaurants-hospitality', label: 'Restaurants & Hospitality' },
      { value: 'health-wellness', label: 'Health & Wellness' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    field: 'industryOther',
    question: 'Tell us what kind of business you run.',
    type: 'text',
    inputType: 'text',
    placeholder: 'e.g. Accounting firm, gym, distributor',
    required: true,
    showWhen: { field: 'industry', value: 'other' }
  },
  {
    field: 'bottleneck',
    question: 'What would you like to improve first?',
    type: 'options',
    otherField: 'bottleneckOther',
    options: [
      { value: 'manual-reporting', label: 'Manual reporting' },
      { value: 'lead-follow-up', label: 'Lead follow-up' },
      { value: 'inventory-margin', label: 'Inventory or margin visibility' },
      { value: 'admin-workflows', label: 'Admin workflows' },
      { value: 'customer-service', label: 'Customer service' },
      { value: 'other', label: 'Other' }
    ]
  },
  {
    field: 'bottleneckOther',
    question: 'What would you like to improve?',
    type: 'text',
    inputType: 'text',
    placeholder: 'Describe what you want to improve',
    required: true,
    showWhen: { field: 'bottleneck', value: 'other' }
  },
  {
    field: 'tools',
    question: 'Which tools do you already use?',
    type: 'text',
    inputType: 'text',
    placeholder: 'e.g. Shopify, Xero, Excel, HubSpot',
    optionalLabel: 'Not sure'
  },
  {
    field: 'message',
    question: 'Anything else you want us to know?',
    type: 'textarea',
    placeholder: 'Share a goal, problem, or question.',
    optionalLabel: 'No, that is enough'
  },
  {
    field: 'appointmentSlot',
    question: 'Choose a time for your free consultation.',
    type: 'options',
    options: [],
    required: true
  }
];

const appointmentHours = [9, 11, 14, 16];

const buildAppointmentOptions = () => {
  const now = new Date();
  const earliest = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  const options = [];
  const labelFormatter = new Intl.DateTimeFormat(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });

  for (let dayOffset = 1; options.length < 6 && dayOffset < 14; dayOffset += 1) {
    const date = new Date(now);
    date.setDate(now.getDate() + dayOffset);

    const dayOfWeek = date.getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    appointmentHours.forEach((hour) => {
      if (options.length >= 6) return;

      const slot = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0, 0, 0);
      if (slot <= earliest) return;

      options.push({
        value: slot.toISOString(),
        label: labelFormatter.format(slot)
      });
    });
  }

  return options;
};

const SAVED_CONSULTATIONS_KEY = 'mosyniConsultationRequests';
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const saveConsultationRequest = (request) => {
  try {
    const savedRequests = JSON.parse(window.localStorage.getItem(SAVED_CONSULTATIONS_KEY) || '[]');
    window.localStorage.setItem(
      SAVED_CONSULTATIONS_KEY,
      JSON.stringify([request, ...savedRequests].slice(0, 20))
    );
  } catch (error) {
    console.warn('Unable to save consultation request locally', error);
  }
};

const AIServicesLandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [chatStep, setChatStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false); // Track submission state
  const [submitStatus, setSubmitStatus] = useState({ success: null, message: '' }); // Track submission status
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    industry: '',
    industryOther: '',
    bottleneck: '',
    bottleneckOther: '',
    tools: '',
    message: '',
    appointmentSlot: '',
    inquiryType: 'get-started' // Default inquiry type
  });
  const appointmentOptions = useMemo(() => buildAppointmentOptions(), []);
  const activeConsultationSteps = useMemo(
    () => consultationSteps
      .filter(step => !step.showWhen || formData[step.showWhen.field] === step.showWhen.value)
      .map(step => (
        step.field === 'appointmentSlot' ? { ...step, options: appointmentOptions } : step
      )),
    [appointmentOptions, formData]
  );

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  // Reset form data
  const resetForm = () => {
    setChatStep(0);
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      industry: '',
      industryOther: '',
      bottleneck: '',
      bottleneckOther: '',
      tools: '',
      message: '',
      appointmentSlot: '',
      inquiryType: 'get-started'
    });
  };

  // Open modal with specific inquiry type
  const openModal = (type) => {
    setChatStep(0);
    setFormData(prev => ({...prev, inquiryType: type}));
    setShowModal(true);
    // Reset status when opening modal
    setSubmitStatus({ success: null, message: '' });
  };

  // Handle form submission
  const submitForm = (data, e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    const pendingRequest = buildConsultationRequest(data);
    saveConsultationRequest(pendingRequest);

    // Send form data via EmailJS
    sendContactForm(pendingRequest)
      .then((response) => {
        console.log('Email sent successfully!', response);
        saveConsultationRequest({ ...pendingRequest, deliveryStatus: 'sent' });
        setSubmitStatus({
          success: true,
          message: pendingRequest.appointmentLabel
            ? `Thanks, ${data.name}. Your free consultation is set for ${pendingRequest.appointmentLabel}.`
            : 'Thanks. Your free consultation request has been sent.'
        });
        // Clear form but don't close modal immediately to show success message
        resetForm();
        // Close modal after delay to show success message
        setTimeout(() => {
          setShowModal(false);
          setSubmitStatus({ success: null, message: '' });
        }, 6000);
      })
      .catch((error) => {
        console.error('Error sending email:', error);
        saveConsultationRequest({ ...pendingRequest, deliveryStatus: 'saved-email-pending' });
        setSubmitStatus({
          success: false,
          message: 'We could not send this automatically. Please email us at contact@mosyni.com with the details above.'
        });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // Handle form submission
  const handleSubmit = (e) => {
    submitForm(formData, e);
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const currentStep = activeConsultationSteps[chatStep];
  const isFinalStep = chatStep === activeConsultationSteps.length - 1;
  const currentValue = formData[currentStep.field] || '';
  const currentStepComplete = !currentStep.required || String(currentValue).trim().length > 0;
  const currentStepError = currentStep.inputType === 'email' && currentValue.trim() && !emailPattern.test(currentValue.trim())
    ? 'Please enter a valid email address.'
    : '';
  const canContinue = currentStepComplete && !currentStepError;
  const progressPercent = Math.round(((chatStep + 1) / activeConsultationSteps.length) * 100);

  const getDisplayValue = (step, value) => {
    if (!value) return '';
    return step.options?.find((option) => option.value === value)?.label || value;
  };

  const buildConsultationRequest = (data, deliveryStatus = 'pending') => {
    const selectedSlot = appointmentOptions.find(option => option.value === data.appointmentSlot);
    const industryStep = activeConsultationSteps.find(step => step.field === 'industry');
    const bottleneckStep = activeConsultationSteps.find(step => step.field === 'bottleneck');
    const industryLabel = data.industry === 'other'
      ? data.industryOther
      : getDisplayValue(industryStep, data.industry);
    const improvementLabel = data.bottleneck === 'other'
      ? data.bottleneckOther
      : getDisplayValue(bottleneckStep, data.bottleneck);
    const transcript = activeConsultationSteps
      .map(step => {
        const answer = getDisplayValue(step, data[step.field]) || 'Skipped for now';
        return `${step.question}\n${answer}`;
      })
      .join('\n\n');

    return {
      ...data,
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      submittedAt: new Date().toISOString(),
      deliveryStatus,
      industryLabel,
      improvementLabel,
      appointmentLabel: selectedSlot?.label || data.appointmentSlot,
      chatTranscript: transcript
    };
  };

  const setStepValue = (field, value) => {
    setFormData(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  const goToNextStep = () => {
    setChatStep(step => Math.min(step + 1, activeConsultationSteps.length - 1));
  };

  const handleOptionSelect = (value) => {
    setFormData(prevState => ({
      ...prevState,
      [currentStep.field]: value,
      ...(currentStep.otherField && value !== 'other' ? { [currentStep.otherField]: '' } : {})
    }));
    if (!isFinalStep) {
      goToNextStep();
    }
  };

  const handleChatSubmit = (e) => {
    e.preventDefault();
    if (!canContinue) return;

    if (isFinalStep) {
      handleSubmit(e);
      return;
    }

    goToNextStep();
  };

  const handleSkipStep = () => {
    setStepValue(currentStep.field, '');
    if (!isFinalStep) {
      goToNextStep();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white font-sans">
      {/* Navigation */}
      <nav className="flex justify-between items-center px-6 py-4 lg:px-12">
        <div className="flex items-center">
          <span className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">Mosyni AI</span>
        </div>
        <div className="hidden md:flex space-x-8">
          <a href="#services" className="hover:text-blue-400 transition">Services</a>
          <a href="#benefits" className="hover:text-blue-400 transition">Benefits</a>
          <a href="#methodology" className="hover:text-blue-400 transition">Methodology</a>
          <a href="#contact" className="hover:text-blue-400 transition">Contact</a>
        </div>
        <button
          onClick={() => openModal('get-started')}
          className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-full font-semibold transition"
        >
          Get Started
        </button>
      </nav>

      {/* Hero Section */}
      <header className={`px-4 pb-16 pt-12 transition-opacity duration-1000 md:pt-20 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="text-left">
            <p className="mb-4 inline-flex rounded-lg border border-cyan-400/30 bg-cyan-400/10 px-3 py-2 text-sm font-semibold uppercase tracking-wide text-cyan-200">
              AI consulting for small business operations
            </p>
            <h1 className="mb-6 max-w-3xl text-4xl font-bold leading-tight text-white md:text-6xl">
              Build dashboards and automations that save time, protect margin, and show what to do next.
            </h1>
            <p className="mb-8 max-w-2xl text-lg leading-8 text-gray-200 md:text-xl">
              Mosyni designs practical AI systems for small businesses: revenue dashboards, inventory alerts, lead follow-up agents, and workflow automation built around your industry.
            </p>
            <div className="flex flex-col gap-4 sm:flex-row">
              <button
                onClick={() => openModal('diagnosis')}
                className="bg-blue-600 hover:bg-blue-700 px-8 py-3 rounded-lg font-semibold text-lg transition"
              >
                Book a Free Consultation
              </button>
              <button
                onClick={() => {
                  const industriesSection = document.getElementById('industries');
                  industriesSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="bg-transparent border border-white/70 hover:bg-white hover:text-gray-900 px-8 py-3 rounded-lg font-semibold text-lg transition"
              >
                View Industry Solutions
              </button>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-4 text-sm text-gray-100 sm:grid-cols-3">
              <div className="flex items-center">
                <Check className="text-green-400 mr-2 shrink-0" size={20} />
                <span>Built on your existing tools</span>
              </div>
              <div className="flex items-center">
                <Check className="text-green-400 mr-2 shrink-0" size={20} />
                <span>Clear ROI before build</span>
              </div>
              <div className="flex items-center">
                <Check className="text-green-400 mr-2 shrink-0" size={20} />
                <span>Training and handover included</span>
              </div>
            </div>
          </div>

          <InventoryMarginDashboard />
        </div>
      </header>

      {/* Services Section */}
      <section id="services" className="py-16 px-4 md:px-12 max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
          Practical Solutions for Your Business
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <a href="/business-intelligence" className="block">
            <div className="h-full bg-gray-800 bg-opacity-50 p-8 rounded-lg hover:transform hover:scale-105 transition duration-300">
              <div className="bg-blue-600 p-3 rounded-lg inline-block mb-4">
                <BarChart size={24} />
              </div>
              <h3 className="text-xl font-bold mb-3">Decision Dashboards</h3>
              <p className="text-gray-300">
                See revenue, stock, jobs, margins, and customer activity in one clear business view.
              </p>
            </div>
          </a>
          <a href="/process-automation" className="block">
            <div className="h-full bg-gray-800 bg-opacity-50 p-8 rounded-lg hover:transform hover:scale-105 transition duration-300">
              <div className="bg-purple-600 p-3 rounded-lg inline-block mb-4">
                <Zap size={24} />
              </div>
              <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
              <p className="text-gray-300">
                Remove copy-paste work across quotes, invoices, onboarding, reporting, and follow-up.
              </p>
            </div>
          </a>
          <a href="/predictive-analytics" className="block">
            <div className="h-full bg-gray-800 bg-opacity-50 p-8 rounded-lg hover:transform hover:scale-105 transition duration-300">
              <div className="bg-pink-600 p-3 rounded-lg inline-block mb-4">
                <TrendingUp size={24} />
              </div>
              <h3 className="text-xl font-bold mb-3">Forecasting Models</h3>
              <p className="text-gray-300">
                Predict demand, staffing needs, cashflow pressure, and stock risks before they bite.
              </p>
            </div>
          </a>
          <a href="/customer-insights" className="block">
            <div className="h-full bg-gray-800 bg-opacity-50 p-8 rounded-lg hover:transform hover:scale-105 transition duration-300">
              <div className="bg-green-600 p-3 rounded-lg inline-block mb-4">
                <Users size={24} />
              </div>
              <h3 className="text-xl font-bold mb-3">AI Agents</h3>
              <p className="text-gray-300">
                Draft replies, qualify leads, summarize calls, and route tasks while your team stays in control.
              </p>
            </div>
          </a>
        </div>
      </section>

      {/* Industry Solutions Section */}
      <section id="industries" className="px-4 py-16 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-cyan-300">Solutions by industry</p>
            <h2 className="text-3xl font-bold text-white md:text-4xl">Solutions shaped around your industry.</h2>
            <p className="mt-4 text-lg leading-8 text-gray-300">
              Mosyni starts with the work your team handles every day, then creates dashboards, agents, and automations around the numbers that matter to your business.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {industrySolutions.map(({ name, icon: Icon, outcome, examples }) => (
              <div key={name} className="rounded-lg border border-gray-700 bg-gray-800/60 p-6">
                <div className="mb-4 flex items-center gap-3">
                  <div className="rounded-lg bg-blue-600 p-3">
                    <Icon size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{name}</h3>
                    <p className="text-gray-300">{outcome}</p>
                  </div>
                </div>
                <ul className="space-y-2 text-sm text-gray-300">
                  {examples.map((example) => (
                    <li key={example} className="flex items-start">
                      <Check className="mr-2 mt-0.5 shrink-0 text-green-400" size={16} />
                      <span>{example}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-16 px-4 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
            Why Choose Our Solutions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="flex">
              <div className="mr-4">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Award size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Cost-Effective</h3>
                <p className="text-gray-300">
                  Enterprise-level AI capabilities at small business prices. Pay only for what you need, with scalable solutions that grow with your business.
                </p>
              </div>
            </div>
            <div className="flex">
              <div className="mr-4">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Users size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">User-Friendly</h3>
                <p className="text-gray-300">
                  No technical expertise required. Our solutions come with intuitive interfaces designed for business owners and their teams.
                </p>
              </div>
            </div>
            <div className="flex">
              <div className="mr-4">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Zap size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Efficiency Boost</h3>
                <p className="text-gray-300">
                  Reduce manual work by up to 70% and increase decision-making speed by leveraging automated workflows and real-time insights.
                </p>
              </div>
            </div>
            <div className="flex">
              <div className="mr-4">
                <div className="bg-blue-600 p-3 rounded-lg">
                  <Workflow size={24} />
                </div>
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2">Customized Approach</h3>
                <p className="text-gray-300">
                  Each solution is tailored to your specific business needs and industry, ensuring you get relevant results that make a difference.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Automation Flow Section */}
      <section className="px-4 py-16 md:px-12">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.85fr_1.15fr]">
            <div>
              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-cyan-300">From manual work to managed workflow</p>
              <h2 className="text-3xl font-bold text-white md:text-4xl">Save hours each week and respond faster to every opportunity.</h2>
              <p className="mt-4 text-lg leading-8 text-gray-300">
                Mosyni helps your team handle leads, stock, reports, and follow-ups with less manual effort and clearer next steps.
              </p>
            </div>

            <div className="rounded-lg border border-gray-700 bg-gray-800/70 p-6">
              <div className="space-y-4">
                {workflowComparisons.map(({ topic, before, after }) => (
                  <div key={topic} className="rounded-lg border border-gray-700 bg-gray-900/70 p-4">
                    <h3 className="mb-3 text-base font-bold text-white">{topic}</h3>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-lg border border-red-400/30 bg-red-500/10 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-red-200">Before</p>
                        <p className="mt-1 text-sm text-gray-200">{before}</p>
                      </div>
                      <div className="rounded-lg border border-emerald-400/30 bg-emerald-500/10 p-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-200">After</p>
                        <p className="mt-1 text-sm text-gray-200">{after}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-4">
                {automationFlow.map(({ icon: Icon, title, detail }) => (
                  <div key={title} className="rounded-lg bg-gray-900/80 p-4">
                    <Icon className="mb-3 text-blue-300" size={22} />
                    <h4 className="font-bold text-white">{title}</h4>
                    <p className="mt-2 text-sm text-gray-300">{detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section id="methodology" className="py-16 px-4 md:px-12 max-w-6xl mx-auto">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
          Our Implementation Process
        </h2>
        <div className="relative">
          {/* Timeline line */}
          <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-blue-600"></div>

          {/* Timeline items */}
          <div className="space-y-12 relative">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-8 mb-4 md:mb-0 text-right">
                <h3 className="text-xl font-bold mb-2">1. Discovery & Analysis</h3>
                <p className="text-gray-300">
                  We analyze your business processes, identify pain points, and determine how AI can add the most value to your operations.
                </p>
              </div>
              <div className="md:w-1/2 flex justify-start">
                <div className="relative flex items-center justify-center">
                  <div className="h-8 w-8 bg-blue-600 rounded-full z-10 flex items-center justify-center">1</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse items-center">
              <div className="md:w-1/2 md:pl-8 mb-4 md:mb-0 text-left">
                <h3 className="text-xl font-bold mb-2">2. Solution Design</h3>
                <p className="text-gray-300">
                  We create a customized AI solution blueprint that addresses your specific challenges and aligns with your business goals.
                </p>
              </div>
              <div className="md:w-1/2 flex justify-end">
                <div className="relative flex items-center justify-center">
                  <div className="h-8 w-8 bg-blue-600 rounded-full z-10 flex items-center justify-center">2</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-1/2 md:pr-8 mb-4 md:mb-0 text-right">
                <h3 className="text-xl font-bold mb-2">3. Implementation & Training</h3>
                <p className="text-gray-300">
                  We set up your AI solution, integrate it with your existing systems, and train your team on how to use it effectively.
                </p>
              </div>
              <div className="md:w-1/2 flex justify-start">
                <div className="relative flex items-center justify-center">
                  <div className="h-8 w-8 bg-blue-600 rounded-full z-10 flex items-center justify-center">3</div>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row-reverse items-center">
              <div className="md:w-1/2 md:pl-8 mb-4 md:mb-0 text-left">
                <h3 className="text-xl font-bold mb-2">4. Continuous Optimization</h3>
                <p className="text-gray-300">
                  We monitor performance, gather feedback, and fine-tune your solution to ensure it delivers maximum value over time.
                </p>
              </div>
              <div className="md:w-1/2 flex justify-end">
                <div className="relative flex items-center justify-center">
                  <div className="h-8 w-8 bg-blue-600 rounded-full z-10 flex items-center justify-center">4</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 text-center bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8">
            Start leveraging AI technology to increase efficiency, reduce costs, and gain competitive insights.
          </p>
          <button
            onClick={() => openModal('contact')}
            className="bg-white text-gray-900 hover:bg-gray-200 px-8 py-3 rounded-full font-semibold text-lg transition"
          >
            Schedule a Free Consultation
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-gray-900 py-12 px-4">
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8">
        <div>
          <h3 className="text-xl font-bold mb-4">Mosyni AI</h3>
          <p className="text-gray-400">
            AI-powered solutions designed specifically for small businesses to compete and thrive.
          </p>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-4">Services</h3>
          <ul className="space-y-2 text-gray-400">
            <li><a href="/business-intelligence" className="hover:text-blue-400 transition">Business Intelligence</a></li>
            <li><a href="/process-automation" className="hover:text-blue-400 transition">Process Automation</a></li>
            <li><a href="/predictive-analytics" className="hover:text-blue-400 transition">Predictive Analysis</a></li>
            <li><a href="/customer-insights" className="hover:text-blue-400 transition">Customer Insights</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-4">Company</h3>
          <ul className="space-y-2 text-gray-400">
            <li><a href="/about" className="hover:text-blue-400 transition">About Us</a></li>
            <li><a href="/about#expertise" className="hover:text-blue-400 transition">Our Expertise</a></li>
            <li><a href="/about#approach" className="hover:text-blue-400 transition">Our Approach</a></li>
            <li><a href="/about#methodology" className="hover:text-blue-400 transition">Methodology</a></li>
          </ul>
        </div>
        <div>
          <h3 className="text-lg font-semibold mb-4">Contact</h3>
          <ul className="space-y-2 text-gray-400">
            <li>contact@mosyni.com</li>
            <li>Auckland, New Zealand</li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-8 pt-8 border-t border-gray-800 text-center text-gray-500">
        <p>© 2025 Mosyni AI. All rights reserved.</p>
      </div>
    </footer>

      {/* Contact Form Modal - Updated with status feedback */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-lg max-h-[92vh] overflow-y-auto relative">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
              disabled={isSubmitting}
            >
              <CloseIcon size={24} />
            </button>

            <div className="p-6">
              <div className="mb-5 pr-8 text-left">
                <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-600">
                  Book a Free Consultation
                  {formData.inquiryType === 'demo' && " - Dashboard Demo"}
                </h3>
              </div>

              {submitStatus.success === null ? (
                <form onSubmit={handleChatSubmit}>
                  <div className="hidden" aria-hidden="true">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website || ''}
                      onChange={handleInputChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  <div className="mb-5">
                    <div className="mb-2 flex items-center justify-between text-xs text-gray-400">
                      <span>Question {chatStep + 1} of {activeConsultationSteps.length}</span>
                      <span>{progressPercent}% complete</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-gray-700">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 transition-all duration-300"
                        style={{ width: `${progressPercent}%` }}
                      />
                    </div>
                  </div>

                  {chatStep > 0 && (
                    <div className="max-h-[28vh] space-y-4 overflow-y-auto pr-1">
                    {activeConsultationSteps.slice(0, chatStep).map((step) => {
                      const answer = getDisplayValue(step, formData[step.field]) || 'Skipped for now';

                      return (
                        <div key={step.field} className="space-y-2">
                          <div className="flex justify-start">
                            <div className="max-w-[88%] rounded-lg rounded-tl-sm bg-gray-700 px-4 py-3 text-left text-sm text-gray-100">
                              {step.question}
                            </div>
                          </div>
                          <div className="flex justify-end">
                            <div className="max-w-[88%] rounded-lg rounded-tr-sm bg-blue-600 px-4 py-3 text-left text-sm font-medium text-white">
                              {answer}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    </div>
                  )}

                  <div className={`${chatStep > 0 ? 'mt-5' : ''} rounded-lg border border-gray-700 bg-gray-900/80 p-4`}>
                    <div className="mb-4 flex justify-start">
                      <div className="w-full rounded-lg rounded-tl-sm bg-gray-700 px-4 py-3 text-left text-sm font-semibold text-gray-100">
                        {currentStep.question}
                      </div>
                    </div>

                    {currentStep.type === 'options' ? (
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {currentStep.options.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            onClick={() => handleOptionSelect(option.value)}
                            className={`rounded-lg border px-4 py-3 text-left text-sm font-medium transition ${
                              currentValue === option.value
                                ? 'border-blue-400 bg-blue-600 text-white'
                                : 'border-gray-600 bg-gray-800 text-gray-100 hover:border-blue-400 hover:bg-gray-700'
                            }`}
                            disabled={isSubmitting}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div>
                        <label htmlFor={`chat-${currentStep.field}`} className="sr-only">{currentStep.question}</label>
                        {currentStep.type === 'textarea' ? (
                          <textarea
                            id={`chat-${currentStep.field}`}
                            name={currentStep.field}
                            value={currentValue}
                            onChange={handleInputChange}
                            rows="3"
                            placeholder={currentStep.placeholder}
                            className="w-full rounded-lg border border-gray-600 bg-gray-800 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={isSubmitting}
                          />
                        ) : (
                          <>
                            <input
                              type={currentStep.inputType}
                              id={`chat-${currentStep.field}`}
                              name={currentStep.field}
                              value={currentValue}
                              onChange={handleInputChange}
                              placeholder={currentStep.placeholder}
                              required={currentStep.required}
                              aria-invalid={Boolean(currentStepError)}
                              aria-describedby={currentStepError ? `chat-${currentStep.field}-error` : undefined}
                              className={`w-full rounded-lg border bg-gray-800 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 ${
                                currentStepError
                                  ? 'border-red-400 focus:ring-red-400'
                                  : 'border-gray-600 focus:ring-blue-500'
                              }`}
                              disabled={isSubmitting}
                            />
                            {currentStepError && (
                              <p id={`chat-${currentStep.field}-error`} className="mt-2 text-left text-sm text-red-200" role="alert">
                                {currentStepError}
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <button
                        type="button"
                        onClick={() => setChatStep(step => Math.max(step - 1, 0))}
                        className={`rounded-lg border border-gray-600 px-4 py-2 text-sm font-semibold text-gray-200 transition hover:bg-gray-700 ${chatStep === 0 ? 'invisible' : ''}`}
                        disabled={isSubmitting || chatStep === 0}
                      >
                        Back
                      </button>

                      <div className="flex flex-col gap-2 sm:flex-row">
                        {currentStep.optionalLabel && !isFinalStep && (
                          <button
                            type="button"
                            onClick={handleSkipStep}
                            className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-300 transition hover:bg-gray-700"
                            disabled={isSubmitting}
                          >
                            {currentStep.optionalLabel}
                          </button>
                        )}

                        {(currentStep.type !== 'options' || isFinalStep) && (
                          <button
                            type="submit"
                            className="rounded-lg bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-2 text-sm font-bold text-white transition-all duration-200 hover:from-blue-700 hover:to-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isSubmitting || !canContinue}
                          >
                            {isSubmitting ? 'Booking...' : isFinalStep ? 'Book Consultation Time' : 'Continue'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className={`p-4 rounded-md mb-4 ${submitStatus.success ? 'bg-green-700 bg-opacity-30' : 'bg-red-700 bg-opacity-30'}`}>
                  <p className="text-center">
                    {submitStatus.message}
                  </p>
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIServicesLandingPage;
